import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Tables } from '@/integrations/supabase/types';
import { useOptimizedHotel } from '@/contexts/OptimizedHotelContext';

type Room = Tables<'rooms'>;
type Booking = Tables<'booking_core'>;
type Offer = Tables<'offers'>;

interface GuestData {
  guest_id: string;
  full_name?: string;
  email?: string;
  phone?: string;
}

interface SearchResult {
  type: 'room' | 'booking' | 'offer' | 'guest';
  id: string;
  title: string;
  description: string;
  url: string;
  data: Room | Booking | Offer | GuestData;
}

export const useSearch = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const { currentHotel } = useOptimizedHotel();

  const search = async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setResults([]);
      return;
    }

    if (!currentHotel) {
      console.warn('🔍 Нет текущего отеля для поиска');
      setResults([]);
      return;
    }

    console.log('🔍 Начинаем поиск для:', searchQuery, 'в отеле:', currentHotel.name);
    setLoading(true);
    
    try {
      const searchTerm = `%${searchQuery}%`;
      
      // Параллельные запросы: Supabase + Selectel
      const [roomsResult, bookingsResult, offersResult, selectelResult] = await Promise.allSettled([
        // Поиск по номерам
        supabase
          .from('rooms')
          .select('*')
          .eq('hotel_id', currentHotel.id)
          .or(`name.ilike.${searchTerm},description.ilike.${searchTerm},room_type.ilike.${searchTerm}`)
          .limit(5),
        
        // Поиск по бронированиям  
        supabase
          .from('booking_core')
          .select('*')
          .eq('hotel_id', currentHotel.id)
          .or(`guest_id.ilike.${searchTerm},room_type.ilike.${searchTerm},booking_reference.ilike.${searchTerm}`)
          .is('deleted_at', null)
          .limit(5),
        
        // Поиск по предложениям
        supabase
          .from('offers')
          .select('*')
          .eq('hotel_id', currentHotel.id)
          .or(`title.ilike.${searchTerm},description.ilike.${searchTerm}`)
          .limit(5),
        
        // 🆕 Поиск гостей в Selectel (ФИО, email, телефон)
        // Строгая изоляция по hotel_id - админ видит только гостей своего отеля
        supabase.functions.invoke('selectel-proxy', {
          body: {
            operation: 'search_guests',
            hotel_id: currentHotel.id,
            query: searchQuery.trim(),
            limit: 10,
            global_search: false  // Строгая изоляция по отелю
          }
        })
      ]);

      // Извлекаем данные
      const rooms = roomsResult.status === 'fulfilled' ? roomsResult.value.data : [];
      const bookings = bookingsResult.status === 'fulfilled' ? bookingsResult.value.data : [];
      const offers = offersResult.status === 'fulfilled' ? offersResult.value.data : [];
      const selectelGuests = selectelResult.status === 'fulfilled' && selectelResult.value.data?.success 
        ? selectelResult.value.data.guests || [] 
        : [];

      console.log('🔍 Результаты поиска:', { 
        rooms: rooms?.length || 0, 
        bookings: bookings?.length || 0, 
        offers: offers?.length || 0,
        selectelGuests: selectelGuests.length
      });

      // Если найдены гости в Selectel, ищем их бронирования
      let guestBookings: Booking[] = [];
      if (selectelGuests.length > 0) {
        const guestIds = selectelGuests.map((g: GuestData) => g.guest_id);
        
        // Ищем бронирования по guest_id (прямая связь)
        const { data: foundBookings } = await supabase
          .from('booking_core')
          .select('*')
          .eq('hotel_id', currentHotel.id)
          .in('guest_id', guestIds)
          .is('deleted_at', null)
          .limit(20);
        
        guestBookings = foundBookings || [];
        
        // Если бронирований не найдено по guest_id, пробуем найти по имени через booking_guests
        if (guestBookings.length === 0 && selectelGuests.length > 0) {
          // Пробуем через booking_guests таблицу
          const guestIdsFromSelectel = selectelGuests.map((g: GuestData) => g.guest_id);
          const { data: bookingGuestsLinks } = await supabase
            .from('booking_guests')
            .select('booking_id, guest_id')
            .in('guest_id', guestIdsFromSelectel)
            .limit(50);
          
          if (bookingGuestsLinks && bookingGuestsLinks.length > 0) {
            const bookingIdsFromLinks = [...new Set(bookingGuestsLinks.map(bg => bg.booking_id))];
            const { data: linkedBookings } = await supabase
              .from('booking_core')
              .select('*')
              .eq('hotel_id', currentHotel.id)
              .in('id', bookingIdsFromLinks)
              .is('deleted_at', null)
              .limit(20);
            
            guestBookings = linkedBookings || [];
          }
        }
        
        console.log('🔍 Найдено бронирований по гостям из Selectel:', guestBookings.length);
      }

      // Формируем результаты
      const searchResults: SearchResult[] = [
        // Гости из Selectel с их бронями
        ...selectelGuests.map((guest: GuestData) => {
          const guestBookingsForGuest = guestBookings.filter(b => b.guest_id === guest.guest_id);
          const latestBooking = guestBookingsForGuest.sort((a, b) => 
            new Date(b.check_in_date).getTime() - new Date(a.check_in_date).getTime()
          )[0];
          const bookingInfo = guestBookingsForGuest.length > 0 
            ? `${guestBookingsForGuest.length} бронирований` 
            : 'Нет бронирований';
          
          // Deep-link: если есть бронь - сразу на шахматку с датой
          const url = latestBooking 
            ? `/admin/room-grid?bookingId=${latestBooking.id}&date=${latestBooking.check_in_date}`
            : '/admin/bookings-list';
          
          return {
            type: 'guest' as const,
            id: guest.guest_id,
            title: guest.full_name || 'Без имени',
            description: [guest.email, guest.phone, bookingInfo].filter(Boolean).join(' • '),
            url,
            data: { ...guest, latest_booking_status: latestBooking?.status || null }
          };
        }),
        
        // Бронирования гостей (если не дублируются) - deep-link на шахматку
        ...guestBookings
          .filter(booking => !selectelGuests.some((g: GuestData) => g.guest_id === booking.guest_id))
          .slice(0, 5)
          .map(booking => {
            const guest = selectelGuests.find((g: GuestData) => g.guest_id === booking.guest_id);
            return {
              type: 'booking' as const,
              id: booking.id,
              title: guest?.full_name || `Гость: ${booking.guest_id?.slice(0, 20)}...`,
              description: `${booking.room_type} | ${booking.booking_reference || 'Без номера'} | ${new Date(booking.check_in_date).toLocaleDateString()}`,
              url: `/admin/room-grid?bookingId=${booking.id}&date=${booking.check_in_date}`,
              data: booking
            };
          }),
        
        // Номера
        ...(rooms || []).map(room => ({
          type: 'room' as const,
          id: room.id,
          title: room.name,
          description: `${room.room_type}`,
          url: '/admin/rooms',
          data: room
        })),
        
        // Обычные бронирования (если не найдены через Selectel) - deep-link на шахматку
        ...(bookings || [])
          .filter(b => !guestBookings.some(gb => gb.id === b.id))
          .map(booking => ({
            type: 'booking' as const,
            id: booking.id,
            title: `Бронь: ${booking.booking_reference || booking.id.slice(0, 8)}`,
            description: `${booking.room_type} | ${new Date(booking.check_in_date).toLocaleDateString()}`,
            url: `/admin/room-grid?bookingId=${booking.id}&date=${booking.check_in_date}`,
            data: booking
          })),
        
        // Предложения
        ...(offers || []).map(offer => ({
          type: 'offer' as const,
          id: offer.id,
          title: offer.title,
          description: `${offer.offer_type} - скидка ${offer.discount_percentage || 0}%`,
          url: '/admin/offers',
          data: offer
        }))
      ];

      console.log('🔍 Итого результатов:', searchResults.length);
      setResults(searchResults);
    } catch (error) {
      console.error('❌ Критическая ошибка поиска:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;
    
    const delayedSearch = setTimeout(() => {
      if (isMounted) {
        search(query);
      }
    }, 300);

    return () => {
      isMounted = false;
      clearTimeout(delayedSearch);
    };
  }, [query, currentHotel?.id]);

  return {
    query,
    setQuery,
    results,
    loading,
    search
  };
};
